import React = require("react");
import "./SettingsSection.scss";
import {Display} from "../../../components/Display";
import {Icon, icons} from "@vebox/etui-core";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {Buttons} from "../../../components/buttons/Buttons";

interface SettingComponentConfiguration {
    title: string;
    inEditingText: string;
    configuredTitleValue: string;
    notConfiguredTitleValue: string;
    editButtonText: string;
    saveButtonText: string;
    cancelButtonText: string;
    deleteConfButtonText: string;
}

interface SettingComponentProps {
    title: string;
    subtitle?: string;
    configuration: SettingComponentConfiguration;
    isInEditing: boolean;
    onToggle: () => any;
    onSave: () => any;
    onCancel: () => any;
    onDeleteConf: () => any;
    content: JSX.Element;
    editingContent: JSX.Element;
    showSaveButton: boolean;
    allowEditing: boolean;
    contentTitle: string;
}

interface SettingFooterProps {
    showSaveButton: boolean;
    saveButtonText: string;
    cancelButtonText: string;
    deleteConfButtonText: string;
    onSave: () => any;
    onCancel: () => any;
    onDeleteConf: () => any;
}

interface SettingHeaderProps extends SettingFooterProps {
    isInEditing: boolean;
    titleText: string;
    subtitle?: string;
    inEditingText: string;
    currentValue: string;
    editButtonText: string;
    onEditButtonClick: () => any;
    allowEditing: boolean;
}

const SettingFooter: React.SFC<SettingFooterProps> = (props) => {
    return (
        <div className={"settings-footer flx-row flx-align-center flx-end"}>
            <div className={"settings-footer__buttons-container flx-row flx-space-between"} style={{width: 100}}>
                <Display isVisible={props.onDeleteConf}>
                    <Buttons.ComponentButton
                        frontComponent={
                            <Icon glyph={icons.TRASH}/>
                        }
                        onClick={props.onDeleteConf}
                    />
                </Display>
                <Buttons.ComponentButton
                    frontComponent={
                        <Icon glyph={icons.CLOSE}/>
                    }
                    onClick={props.onCancel}
                />
                <Display isVisible={props.showSaveButton}>
                    <Buttons.ComponentButton
                        frontComponent={
                            <span className="material-icons-outlined">done</span>
                        }
                        onClick={props.onSave}
                    />
                </Display>
            </div>
        </div>
    );
};

const SettingHeader: React.SFC<SettingHeaderProps> = (props) => {
    return (
        <VeBoxCard.Header
            className={"flx-row flx-space-between flx-align-center"}
            style={props.isInEditing ? {
                padding: 5,
            } : {
                padding: "3px 10px",
                paddingRight: 5
            }}
            title={props.titleText + (props.currentValue ? " - " + props.currentValue : "")}
            back={
                <>
                    <Display isVisible={!props.isInEditing && props.allowEditing}>
                        <Buttons.ComponentButton
                            frontComponent={
                                <span className="material-icons-outlined">mode_edit</span>
                            }
                            onClick={props.onEditButtonClick}
                        />
                    </Display>
                </>
            }
        />
    );
};


/**
 * A generic settings component composed like
 * <component>
 *      <header>
 *      <content - footer>
 *          <content> // passed to the element with props from outside
 *          <footer>
 *      </content - footer>
 * </component>
 *
 */
export const SettingSection: React.SFC<SettingComponentProps> = (props) => {
    const contentFooterClassName = "settings-body" + (!props.isInEditing ? " closed" : "");
    return (
        <VeBoxCard className={"no-margin settings-section"}>
            <SettingHeader
                titleText={props.title}
                subtitle={props.subtitle}
                inEditingText={props.configuration.inEditingText}
                currentValue={props.contentTitle}
                editButtonText={props.configuration.editButtonText}
                onEditButtonClick={props.onToggle}
                allowEditing={props.allowEditing}
                isInEditing={props.isInEditing}
                saveButtonText={props.configuration.saveButtonText}
                cancelButtonText={props.configuration.cancelButtonText}
                deleteConfButtonText={props.configuration.deleteConfButtonText}
                onCancel={props.onCancel}
                onSave={props.onSave}
                onDeleteConf={props.onDeleteConf}
                showSaveButton={props.showSaveButton}
            />
            <VeBoxCard.Body>
                {!props.isInEditing && props.content != null && props.content}
                <div className={contentFooterClassName}>
                    {props.editingContent}
                    <SettingFooter
                        saveButtonText={props.configuration.saveButtonText}
                        cancelButtonText={props.configuration.cancelButtonText}
                        deleteConfButtonText={props.configuration.deleteConfButtonText}
                        onCancel={props.onCancel}
                        onSave={props.onSave}
                        onDeleteConf={props.onDeleteConf}
                        showSaveButton={props.showSaveButton}
                    />
                </div>
            </VeBoxCard.Body>
        </VeBoxCard>
    );
};
